import React, { useState } from 'react'
import { Calendar as CalendarIcon, ChevronDown, Check, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'

export interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

interface ReusableDateRangePickerProps {
  value?: DateRange
  onSelect: (range: DateRange) => void
  placeholder?: string
  className?: string
}

const ReusableDateRangePicker: React.FC<ReusableDateRangePickerProps> = ({
  value,
  onSelect,
  placeholder = 'Select date range',
  className,
}) => {
  const [range, setRange] = useState<DateRange>(
    value || { from: undefined, to: undefined }
  )
  React.useEffect(() => {
    if (value) setRange(value)
  }, [value])

  const [isOpen, setIsOpen] = useState(false)
  const [activePreset, setActivePreset] = useState<string | null>(null)

  const handlePresetSelect = (preset: string) => {
    const to = new Date()
    let from = new Date()

    switch (preset) {
      case '1d':
        from = dayjs().subtract(1, 'day').toDate()
        break
      case '1w':
        from = dayjs().subtract(1, 'week').toDate()
        break
      case '1m':
        from = dayjs().subtract(1, 'month').toDate()
        break
      case '1y':
        from = dayjs().subtract(1, 'year').toDate()
        break
      default:
        return
    }

    const newRange = { from, to }
    setRange(newRange)
    setActivePreset(preset)
    onSelect(newRange)
    setIsOpen(false)
  }

  const handleCustomSelect = (newRange: DateRange) => {
    setRange(newRange)
    setActivePreset(null)
    onSelect(newRange)
  }

  const clearRange = () => {
    const cleared = { from: undefined, to: undefined }
    setRange(cleared)
    setActivePreset(null)
    onSelect(cleared)
  }

  return (
    <div className={cn('relative min-w-0', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              'h-9 w-full border-border rounded-lg bg-card font-normal text-xs justify-start px-3 transition-all hover:bg-accent/30 shadow-none relative',
              !range.from && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground shrink-0' />
            <span className='truncate text-left flex-1 min-w-0'>
              {range.from ? (
                range.to ? (
                  <>
                    {dayjs(range.from).format('MMM DD, YYYY')} –{' '}
                    {dayjs(range.to).format('MMM DD, YYYY')}
                  </>
                ) : (
                  dayjs(range.from).format('MMM DD, YYYY')
                )
              ) : (
                placeholder
              )}
            </span>
            {range.from ? (
              <span
                role='button'
                tabIndex={0}
                aria-label='Clear date range'
                className='ml-2 h-4 w-4 shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground'
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  clearRange()
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    clearRange()
                  }
                }}
              >
                <X className='h-3.5 w-3.5' />
              </span>
            ) : (
              <ChevronDown className='ml-2 h-3.5 w-3.5 shrink-0 opacity-50' />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[calc(100vw-32px)] sm:w-[480px] p-0 rounded-2xl border-border shadow-2xl bg-popover/95 backdrop-blur-xl overflow-hidden'
          align='end'
          sideOffset={8}
        >
          <div className='flex flex-col sm:flex-row'>
            {/* Sidebar with presets */}
            <div className='w-full sm:w-[140px] border-b sm:border-b-0 sm:border-r border-border bg-muted/30 p-3 flex flex-row sm:flex-col gap-1.5 overflow-x-auto sm:overflow-x-visible'>
              <div className='hidden sm:block text-[10px] font-bold uppercase text-muted-foreground/60 mb-2 mt-1 px-2 tracking-[0.2em]'>
                Presets
              </div>
              {[
                { id: '1d', label: 'Last 24 Hours' },
                { id: '1w', label: 'Last 7 Days' },
                { id: '1m', label: 'Last 30 Days' },
                { id: '1y', label: 'Last Year' },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => handlePresetSelect(p.id)}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 text-[11px] font-bold transition-all text-left rounded-xl whitespace-nowrap',
                    activePreset === p.id
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {p.label}
                  {activePreset === p.id && (
                    <Check className='w-3.5 h-3.5 hidden sm:block' />
                  )}
                </button>
              ))}
              <div className='sm:mt-auto border-l sm:border-l-0 sm:border-t border-border sm:pt-3 flex items-center'>
                <button
                  onClick={clearRange}
                  className='flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-red-500 hover:bg-red-500/10 rounded-xl w-full text-left transition-colors'
                >
                  <X className='w-3.5 h-3.5' />
                  Reset
                </button>
              </div>
            </div>

            {/* Calendar area */}
            <div className='flex-1 pl-4 pr-6 py-4 flex flex-col min-w-0'>
              <div className='flex items-center justify-between mb-3 border-b border-border/50 pb-2'>
                <div className='text-[10px] font-bold uppercase text-muted-foreground/60 tracking-[0.2em]'>
                  Custom Selection
                </div>
              </div>
              <div className='flex-1 flex justify-center items-center'>
                <Calendar
                  mode='range'
                  captionLayout='dropdown'
                  fromYear={2020}
                  toYear={2030}
                  selected={{ from: range.from, to: range.to }}
                  onSelect={r =>
                    handleCustomSelect(
                      r
                        ? { from: r.from, to: r.to }
                        : { from: undefined, to: undefined }
                    )
                  }
                  numberOfMonths={1}
                  disabled={{ after: new Date() }}
                  className='p-0'
                  classNames={{
                    day_selected:
                      '!bg-primary !text-primary-foreground hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground rounded-lg',
                    day_today:
                      '!bg-muted !text-foreground font-black rounded-lg',
                    day_range_middle:
                      '!bg-primary/10 !text-primary rounded-none',
                    day_range_start: 'rounded-l-lg',
                    day_range_end: 'rounded-r-lg',
                  }}
                />
              </div>
              <div className='mt-4 p-3 bg-muted/50 rounded-xl border border-border/50 flex items-center justify-between gap-3'>
                <div className='flex flex-col gap-0.5'>
                  <span className='text-[9px] font-bold uppercase text-muted-foreground/60 tracking-wider'>
                    Active Range
                  </span>
                  <span className='text-[11px] font-bold text-foreground tabular-nums whitespace-nowrap'>
                    {range.from ? dayjs(range.from).format('DD MMM YY') : '--'}{' '}
                    – {range.to ? dayjs(range.to).format('DD MMM YY') : '--'}
                  </span>
                </div>
                <Button
                  size='sm'
                  className='h-8 rounded-lg bg-primary text-primary-foreground text-[10px] font-bold px-4 hover:opacity-90 shadow-lg shadow-primary/10 transition-all'
                  onClick={() => setIsOpen(false)}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default ReusableDateRangePicker
